import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Save, X as XIcon, Pencil, Search,
  Globe, Link2,
} from "lucide-react";

import {
  fetchChannelMaster, patchChannel,
  type ChannelMasterRow, type ChannelMasterPatch, type AuthProfileOption,
} from "../api/crawlerOps";
import { explainError } from "../api/client";
import { categoryKey, CATEGORY_LABEL, CATEGORY_ORDER, type Category } from "../lib/publishers";

/* ──────────────────────────────────────────────────────────────────────
   /crawler-ops/channels — Channel Master.

   The new source of truth for "how to crawl this publisher" — backed by
   crawler.Channel_Crawler (created in migration 014). Each row covers
   one channel; the bound AuthProfile drives session sharing across
   editions; URL templates apply to every edition unless that edition's
   CrawlerConfig row has an explicit override.

   Phase 3 (2026-05-26) of the Channel_Crawler refactor.
   ────────────────────────────────────────────────────────────────────── */

export function CrawlerChannelMaster() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["crawler/channels"],
    queryFn: fetchChannelMaster,
    refetchInterval: 60_000,
  });

  const [editing, setEditing] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<Category>("all");
  const [language, setLanguage] = useState("");
  const [city, setCity] = useState("");
  const [review, setReview] = useState<"any" | "true" | "false">("any");

  const editingRow = useMemo(
    () => q.data?.channels.find(c => c.channel_id === editing) ?? null,
    [q.data, editing],
  );

  /* Count channels per category bucket — used in tab labels.
     Counts are taken from the full channel set (before search/tab filter)
     so the user always sees how big each bucket is. */
  const tabCounts = useMemo(() => {
    const c: Record<Category, number> = {
      all: 0, regional: 0, mainline: 0, financial: 0, periodical: 0,
    };
    if (!q.data) return c;
    c.all = q.data.channels.length;
    for (const ch of q.data.channels) {
      const k = categoryKey(ch.category);
      if (k) c[k]++;
    }
    return c;
  }, [q.data]);

  /* Distinct languages + cities across the full channel set, with channel
     counts — populate the filter dropdowns. */
  const languageOptions = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of q.data?.channels ?? []) {
      const l = (c.language ?? "").trim();
      if (l) m.set(l, (m.get(l) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [q.data]);

  const cityOptions = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of q.data?.channels ?? []) {
      for (const ct of c.cities) m.set(ct, (m.get(ct) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [q.data]);

  const filtered = useMemo(() => {
    if (!q.data) return [];
    const s = search.trim().toLowerCase();
    const ct = city.trim().toLowerCase();
    let rows = q.data.channels;
    if (tab !== "all") {
      rows = rows.filter(c => categoryKey(c.category) === tab);
    }
    if (language) {
      rows = rows.filter(c => (c.language ?? "").trim() === language);
    }
    if (ct) {
      // "what all papers are there in Ahmedabad" — substring match so a
      // partial typed value already narrows the list
      rows = rows.filter(c => c.cities.some(x => x.toLowerCase().includes(ct)));
    }
    if (review !== "any") {
      rows = rows.filter(c => (c.needs_review_count > 0) === (review === "true"));
    }
    if (s) {
      rows = rows.filter(c =>
        c.channel_name.toLowerCase().includes(s) ||
        (c.category ?? "").toLowerCase().includes(s) ||
        (c.parser_key ?? "").toLowerCase().includes(s) ||
        (c.auth_profile_label ?? "").toLowerCase().includes(s)
      );
    }
    return rows;
  }, [q.data, search, tab, language, city, review]);

  const filtersActive = language !== "" || city !== "" || review !== "any" || search !== "";

  return (
    <div className="p-6 max-w-[1280px] mx-auto">
      <header className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">
          Crawler · Channel Master
        </div>
        <h1 className="font-display text-[28px] leading-tight">Channel-level recipes & bindings</h1>
        <p className="font-sans text-[13px] text-[var(--fg-muted)] mt-1 max-w-[800px]">
          One row per publisher. Bind a session ("Times Group SSO covers TOI/ET/MT/Mirrors")
          via the dropdown, toggle <strong>Needs login</strong>, and edit the URL templates
          that apply to every edition of the channel. Edition-level overrides still live
          in CrawlerConfig.
        </p>
      </header>

      {/* Category tabs — Financial / Mainline / Regional / Periodical */}
      <nav
        role="tablist"
        aria-label="Channel category"
        className="flex items-center gap-px border-b mb-3"
        style={{ borderColor: "var(--rule)" }}
      >
        {CATEGORY_ORDER.map((c) => {
          const active = tab === c;
          const n = tabCounts[c];
          return (
            <button
              key={c}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(c)}
              className="px-3 py-1.5 font-mono text-[11px] uppercase tracking-folio relative transition-colors"
              style={{
                color: active ? "var(--fg)" : "var(--fg-muted)",
                background: active ? "var(--bg-card)" : "transparent",
              }}
            >
              {CATEGORY_LABEL[c]}
              <span className="text-[var(--fg-faint)] ml-1.5 tabular-nums">{n}</span>
              {active && (
                <span
                  aria-hidden
                  className="absolute left-0 right-0 -bottom-[1px] h-[2px]"
                  style={{ background: "var(--accent)" }}
                />
              )}
            </button>
          );
        })}
      </nav>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--fg-faint)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search channel name / parser / session…"
            className="w-full pl-7 pr-2 py-1.5 text-[12px] font-mono"
            style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
          />
        </div>

        <label className="inline-flex items-center gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">Language</span>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="font-mono text-[11px] px-1.5 py-1 cursor-pointer max-w-[160px]"
            style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
          >
            <option value="">All</option>
            {languageOptions.map(([l, n]) => (
              <option key={l} value={l}>{l} ({n})</option>
            ))}
          </select>
        </label>

        <label className="inline-flex items-center gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">Edition / City</span>
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            list="channel-city-options"
            placeholder="e.g. Ahmedabad"
            className="px-2 py-1 text-[11px] font-mono w-[150px]"
            style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
          />
          <datalist id="channel-city-options">
            {cityOptions.map(([ct, n]) => (
              <option key={ct} value={ct}>{`${n} channel${n === 1 ? "" : "s"}`}</option>
            ))}
          </datalist>
        </label>

        <label className="inline-flex items-center gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">Review</span>
          <select
            value={review}
            onChange={(e) => setReview(e.target.value as typeof review)}
            className="font-mono text-[11px] px-1.5 py-1 cursor-pointer"
            style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
          >
            <option value="any">All</option>
            <option value="true">Needs review</option>
            <option value="false">Clean</option>
          </select>
        </label>

        {filtersActive && (
          <button
            onClick={() => { setSearch(""); setLanguage(""); setCity(""); setReview("any"); }}
            className="np-btn-ghost text-[10px]"
            title="Reset all filters"
          >
            Reset
          </button>
        )}

        {q.data && (
          <span className="font-mono text-[11px] text-[var(--fg-faint)] ml-auto">
            {filtered.length} / {q.data.channels.length} channels
          </span>
        )}
      </div>

      {q.isLoading && (
        <div className="font-mono text-[11px] text-[var(--fg-faint)]">loading…</div>
      )}
      {q.isError && (
        <div className="np-banner">
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
          {explainError(q.error)}
        </div>
      )}

      {q.data && (
        <div className="np-card overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--rule-strong)" }}>
                <Th>Channel</Th>
                <Th>Needs login</Th>
                <Th>Bound session</Th>
                <Th>Strategy / Parser</Th>
                <Th>Editions</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <Row
                  key={c.channel_id}
                  row={c}
                  options={q.data.auth_profile_options}
                  cityFilter={city.trim().toLowerCase()}
                  onEdit={() => setEditing(c.channel_id)}
                  onChanged={() => qc.invalidateQueries({ queryKey: ["crawler/channels"] })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingRow && (
        <EditDialog
          row={editingRow}
          options={q.data!.auth_profile_options}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["crawler/channels"] });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/* ─── Row ────────────────────────────────────────────────────────────── */
function Row({
  row, options, cityFilter, onEdit, onChanged,
}: {
  row: ChannelMasterRow;
  options: AuthProfileOption[];
  cityFilter: string;
  onEdit: () => void;
  onChanged: () => void;
}) {
  // Inline NeedsLogin toggle + Bound-session dropdown for fast edits
  const inlinePatch = useMutation({
    mutationFn: (patch: ChannelMasterPatch) =>
      patchChannel(row.channel_id, patch),
    onSuccess: () => onChanged(),
  });

  const opt = options.find(o => o.id === row.auth_profile_id);

  return (
    <tr style={{ borderTop: "1px solid var(--rule)" }}>
      <Td>
        <div className="font-display text-[14px]">{row.channel_name}</div>
        <div className="font-mono text-[10px] text-[var(--fg-faint)] mt-0.5">
          ch{row.channel_id}
          {row.category && <span className="ml-2">· {row.category}</span>}
          {row.language && <span className="ml-2">· {row.language}</span>}
          {row.epaper_url && (
            <a href={row.epaper_url} target="_blank" rel="noreferrer"
               className="ml-2 text-[var(--primary)] hover:underline inline-flex items-center gap-1">
              <Globe size={9} /> {new URL(row.epaper_url).host}
            </a>
          )}
        </div>
      </Td>
      <Td>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={row.needs_login}
            onChange={(e) => inlinePatch.mutate({ needs_login: e.target.checked })}
            disabled={inlinePatch.isPending}
            className="cursor-pointer"
          />
          <span className="font-mono text-[10px] uppercase tracking-folio">
            {row.needs_login ? "yes" : "no"}
          </span>
        </label>
      </Td>
      <Td>
        <select
          value={row.auth_profile_id ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") {
              inlinePatch.mutate({ explicit_unset_auth_profile: true });
            } else {
              inlinePatch.mutate({ auth_profile_id: Number(v) });
            }
          }}
          disabled={inlinePatch.isPending}
          className="font-mono text-[11px] px-1.5 py-0.5 cursor-pointer max-w-[260px]"
          style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
        >
          <option value="">(none — anonymous)</option>
          {options.map(o => (
            <option key={o.id} value={o.id}>
              {o.label}{o.has_session ? " ✓" : ""}
            </option>
          ))}
        </select>
        {opt && opt.has_session && (
          <div className="font-mono text-[9px] text-[var(--primary)] mt-0.5">
            <Link2 size={8} className="inline mr-0.5" />
            has session captured
          </div>
        )}
      </Td>
      <Td>
        <div className="font-mono text-[11px]">{row.strategy_type ?? "—"}</div>
        <div className="font-mono text-[10px] text-[var(--fg-faint)] mt-0.5">
          {row.parser_key ?? "(per-ed)"}
        </div>
      </Td>
      <Td>
        <span className="font-mono text-[11px]">
          {row.crawlable_edition_count}/{row.edition_count}
        </span>
        <span className="font-mono text-[9px] text-[var(--fg-faint)] ml-1">crawlable</span>
        {cityFilter && (
          <div className="font-mono text-[10px] text-[var(--primary)] mt-0.5">
            {row.cities.filter(x => x.toLowerCase().includes(cityFilter)).join(" · ")}
          </div>
        )}
        {row.needs_review_count > 0 && (
          <div className="font-mono text-[9px] mt-0.5" style={{ color: "var(--accent)" }}>
            {row.needs_review_count} need{row.needs_review_count === 1 ? "s" : ""} review
          </div>
        )}
      </Td>
      <Td className="text-right pr-4 whitespace-nowrap">
        <button
          onClick={onEdit}
          className="np-btn-ghost text-[11px]"
          title="Edit full recipe (URL templates, headers, etc.)"
        >
          <Pencil size={10} className="inline mr-1" />Edit
        </button>
      </Td>
    </tr>
  );
}

/* ─── Edit Dialog ──────────────────────────────────────────────────── */
function EditDialog({
  row, options: _options, onClose, onSaved,
}: {
  row: ChannelMasterRow;
  options: AuthProfileOption[];   // reserved for future expansion (e.g. inline session create)
  onClose: () => void;
  onSaved: () => void;
}) {
  // Local form state — only fields the operator touches end up in the patch
  const [draft, setDraft] = useState<ChannelMasterPatch>({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  const setF = <K extends keyof ChannelMasterPatch>(k: K, v: ChannelMasterPatch[K]) =>
    setDraft(d => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: () => patchChannel(row.channel_id, draft),
    onSuccess: () => onSaved(),
  });

  const dirty = Object.keys(draft).length > 0;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="np-card w-full max-w-[680px] max-h-[88vh] overflow-y-auto"
      >
        <div className="px-5 py-3 flex items-center justify-between"
             style={{ borderBottom: "1px solid var(--rule)" }}>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
              Channel · ch{row.channel_id}
            </div>
            <div className="font-display text-[18px]">{row.channel_name}</div>
          </div>
          <button onClick={onClose} className="np-btn-ghost">
            <XIcon size={14} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <Field label="ePaper URL" hint="The public landing page (used by Sessions to derive start_url)">
            <input
              type="text"
              defaultValue={row.epaper_url ?? ""}
              onChange={(e) => setF("epaper_url", e.target.value || null)}
              placeholder="https://epaper.example.com/"
              className="w-full px-2 py-1.5 text-[12px] font-mono"
              style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Strategy">
              <input
                type="text"
                defaultValue={row.strategy_type ?? ""}
                onChange={(e) => setF("strategy_type", e.target.value || null)}
                placeholder="api_json / bs_aspnet / …"
                className="w-full px-2 py-1.5 text-[12px] font-mono"
                style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
              />
            </Field>
            <Field label="Parser key">
              <input
                type="text"
                defaultValue={row.parser_key ?? ""}
                onChange={(e) => setF("parser_key", e.target.value || null)}
                placeholder="toi_v1 / bs_aspnet_v1 / recipe_v1 / …"
                className="w-full px-2 py-1.5 text-[12px] font-mono"
                style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
              />
            </Field>
          </div>

          <Field label="Manifest URL template"
                 hint="Channel-level default. Per-edition CrawlerConfig overrides win when present.">
            <input
              type="text"
              defaultValue={row.manifest_url_template ?? ""}
              onChange={(e) => setF("manifest_url_template", e.target.value || null)}
              placeholder="https://… {PUB} {CITY} {YYYY} {MM} {DD} …"
              className="w-full px-2 py-1.5 text-[12px] font-mono"
              style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
            />
          </Field>

          <Field label="Page URL template">
            <input
              type="text"
              defaultValue={row.page_url_template ?? ""}
              onChange={(e) => setF("page_url_template", e.target.value || null)}
              placeholder="https://… {PUB} {CITY} {YYYY} {MM} {DD} {PAGENAME} …"
              className="w-full px-2 py-1.5 text-[12px] font-mono"
              style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
            />
          </Field>

          <button
            type="button"
            onClick={() => setShowAdvanced(s => !s)}
            className="np-btn-ghost text-[11px]"
          >
            {showAdvanced ? "▼" : "▶"} Advanced (headers / publication code / quality)
          </button>

          {showAdvanced && (
            <div className="space-y-3 pl-3"
                 style={{ borderLeft: "2px solid var(--rule)" }}>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Referer header">
                  <input type="text" defaultValue={row.referer_header ?? ""}
                         onChange={(e) => setF("referer_header", e.target.value || null)}
                         className="w-full px-2 py-1.5 text-[12px] font-mono"
                         style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }} />
                </Field>
                <Field label="Origin header">
                  <input type="text" defaultValue={row.origin_header ?? ""}
                         onChange={(e) => setF("origin_header", e.target.value || null)}
                         className="w-full px-2 py-1.5 text-[12px] font-mono"
                         style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }} />
                </Field>
              </div>
              <Field label="User-Agent override">
                <input type="text" defaultValue={row.user_agent_override ?? ""}
                       onChange={(e) => setF("user_agent_override", e.target.value || null)}
                       className="w-full px-2 py-1.5 text-[12px] font-mono"
                       style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }} />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Publication code">
                  <input type="text" defaultValue={row.publication_code ?? ""}
                         onChange={(e) => setF("publication_code", e.target.value || null)}
                         className="w-full px-2 py-1.5 text-[12px] font-mono"
                         style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }} />
                </Field>
                <Field label="JPEG quality">
                  <input type="number" min={1} max={100}
                         defaultValue={row.jpeg_quality ?? ""}
                         onChange={(e) => setF("jpeg_quality",
                           e.target.value === "" ? null : Number(e.target.value))}
                         className="w-full px-2 py-1.5 text-[12px] font-mono"
                         style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }} />
                </Field>
                <Field label="Timeout (s)">
                  <input type="number" min={1}
                         defaultValue={row.timeout_seconds ?? ""}
                         onChange={(e) => setF("timeout_seconds",
                           e.target.value === "" ? null : Number(e.target.value))}
                         className="w-full px-2 py-1.5 text-[12px] font-mono"
                         style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }} />
                </Field>
              </div>
            </div>
          )}

          {save.isError && (
            <div className="np-banner">
              <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
              {explainError(save.error)}
            </div>
          )}
        </div>

        <div className="px-5 py-3 flex items-center justify-end gap-2"
             style={{ borderTop: "1px solid var(--rule)" }}>
          <button onClick={onClose} className="np-btn-ghost">
            <XIcon size={10} className="inline mr-1" />Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="np-btn text-[12px]"
          >
            {save.isPending
              ? "saving…"
              : <><Save size={10} className="inline mr-1" />
                  Save{dirty ? ` (${Object.keys(draft).length})` : ""}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────── */
function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`text-left px-3 py-2 font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}

function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">
        {label}
      </div>
      {children}
      {hint && <div className="font-mono text-[10px] text-[var(--fg-muted)] mt-1">{hint}</div>}
    </label>
  );
}
